import {isLoggedIn} from '@github-ui/client-env'
import {graphql, useFragment} from 'react-relay'

import type {IssueSidebarLazySections$key} from './__generated__/IssueSidebarLazySections.graphql'
import type {IssueViewerViewer$data} from './__generated__/IssueViewerViewer.graphql'
import {DevelopmentSection, DevelopmentSectionFallback} from './sections/development-section/DevelopmentSection'
import {ParticipantsSection} from './sections/ParticipantsSection'
import {
  RelationshipsSectionFallback,
  RelationshipsSectionInternal,
} from './sections/relations-section/RelationshipsSection'
import {SubscriptionSection, SubscriptionSectionFallback} from './sections/subscription-section/SubscriptionSection'

type IssueSidebarLazySectionsBaseProps = {
  viewer: IssueViewerViewer$data | null
  onLinkClick?: (event: MouseEvent) => void
  onIssueUpdate: () => void
  insideSidePanel?: boolean
  readonly: boolean
}

type IssueSidebarLazySectionsProps = IssueSidebarLazySectionsBaseProps & {
  issueSidebarSecondaryKey?: IssueSidebarLazySections$key
}

export function IssueSidebarLazySections({
  issueSidebarSecondaryKey,
  onIssueUpdate,
  onLinkClick,
  insideSidePanel,
  viewer,
  readonly,
}: IssueSidebarLazySectionsProps) {
  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment IssueSidebarLazySections on Issue {
        ...DevelopmentSectionFragment
        ...RelationshipsSectionFragment
        ...SubscriptionSectionFragment
        ...SubscriptionSectionRefetchableFragment
        ...ParticipantsSectionFragment
      }
    `,
    issueSidebarSecondaryKey,
  )

  if (!data) {
    return <IssueSidebarLazySectionsFallback />
  }

  return (
    <>
      <RelationshipsSectionInternal
        issue={data}
        onLinkClick={onLinkClick}
        insideSidePanel={insideSidePanel}
        readonly={readonly}
        onSelection={onIssueUpdate}
      />
      <DevelopmentSection issue={data} onSelection={onIssueUpdate} readonly={readonly} />
      {isLoggedIn() && <SubscriptionSection issue={data} viewer={viewer} />}
      <ParticipantsSection issue={data} />
    </>
  )
}

export function IssueSidebarLazySectionsFallback() {
  return (
    <>
      <RelationshipsSectionFallback />
      <DevelopmentSectionFallback />
      {isLoggedIn() && <SubscriptionSectionFallback />}
    </>
  )
}
